/**
 * AuthCallback — brand-restyled magic-link verification screen (Phase 3).
 *
 * Visual design (§4 Global, §1 brand):
 *   - Full brand bg, centered, TITAN wordmark (amber), amber spinner.
 *   - "Signing you in…" caption in serif italic, muted ink.
 *   - data-theme inherited from <html> via ThemeScript — tokens apply automatically.
 *
 * LOGIC UNCHANGED — reads ?token= from query string, POSTs to /api/console/auth/verify,
 * stores the session_token, then redirects to / (or /login on failure).
 */
import { useEffect, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { apiFetch, session } from '../lib/api.js';

export default function AuthCallback() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState('verifying');

  useEffect(() => {
    const code = params.get('token');
    if (!code) { setStatus('invalid'); return; }

    apiFetch('/api/console/auth/verify', { method: 'POST', body: { token: code } })
      .then((data) => {
        session.token = data.session_token;
        setStatus('ok');
      })
      .catch(() => setStatus('invalid'));
  }, [params]);

  /* ── Redirect once resolved ──────────────────────────────────────────────── */
  if (status === 'ok')      return <Navigate to="/" replace />;
  if (status === 'invalid') return <Navigate to="/login" replace />;

  /* ── Loading state ───────────────────────────────────────────────────────── */
  return (
    <div
      style={{
        minHeight:      '100dvh',
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        gap:            '24px',
        background:     'var(--paper)',
        color:          'var(--ink)',
        padding:        '24px',
      }}
    >
      {/* TITAN wordmark */}
      <div aria-hidden="true" className="d-xl" style={{ fontSize: '36px', letterSpacing: '0.06em', color: 'var(--acc)' }}>
        TITAN
      </div>

      {/* Amber spinner */}
      <div
        aria-label="Loading"
        role="status"
        style={{
          width:        '36px',
          height:       '36px',
          borderRadius: '50%',
          border:       '3px solid var(--line)',
          borderTopColor:'var(--acc)',
          animation:    'spinner-spin 0.8s linear infinite',
          flexShrink:   0,
        }}
      />

      {/* Caption */}
      <p className="serif-i" style={{ margin: 0, fontSize: '17px', color: 'var(--ink-soft)', lineHeight: 1.5, textAlign: 'center' }}>
        Signing you in…
      </p>
    </div>
  );
}
